import { defineStore } from 'pinia'

export interface Notificacao {
  id: string
  mensagem: string
  relevancia: string
  tipo: string
  visualizado: number
  vista: boolean
  link: string
  created_at: string
  updated_at: string
}

export const useNotificacaoStore = defineStore('notificacao', {
  state: () => ({
    notificacoes: [] as Notificacao[],
    totalNotificacoes: 0,
    loading: false,
    paginaAtual: 1,
    totalPaginas: 1,
  }),

  getters: {
    notificacoesNaoVistas (): Notificacao[] {
      return this.notificacoes.filter(notificacao => !notificacao.vista)
    },

    totalNaoVistas (): number {
      return this.notificacoesNaoVistas.length
    },

    notificacoesRecentes (): Notificacao[] {
      return this.notificacoes.slice(0, 5)
    },
  },

  actions: {
    stripHtml (html: string): string {
      if (!html) { return '' }
      // Remove tags HTML e decodifica entidades HTML
      const div = document.createElement('div')
      div.innerHTML = html
      const text = div.textContent || div.innerText || ''
      return text.trim()
    },

    async carregarNotificacoes (pagina: number = 1) {
      this.loading = true

      try {
        const response = await useApiStore().request(`notificacao?pagina=${pagina}`)

        if (response.data) {
          // Limpa HTML das mensagens antes de armazenar
          this.notificacoes = response.data.map((notif: Notificacao) => ({
            ...notif,
            mensagem: this.stripHtml(notif.mensagem),
          }))
          this.totalNotificacoes = response.total || 0
          this.paginaAtual = response.current_page || 1
          this.totalPaginas = response.last_page || 1
        }
      } catch (error) {
        console.error('Erro ao carregar notificações:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    async marcarComoVista (notificacaoId: string) {
      try {
        await useApiStore().request(`notificacao/vista/${notificacaoId}`, {
          method: 'POST',
        })

        // Atualizar o estado local
        const notificacao = this.notificacoes.find(n => n.id === notificacaoId)
        if (notificacao) {
          notificacao.vista = true
        }
      } catch (error) {
        console.error('Erro ao marcar notificação como vista:', error)
        throw error
      }
    },

    async marcarComoLida (notificacaoId: string) {
      try {
        await useApiStore().request(`notificacao/ler/${notificacaoId}`, {
          method: 'POST',
        })

        // Atualizar o estado local
        const notificacao = this.notificacoes.find(n => n.id === notificacaoId)
        if (notificacao) {
          notificacao.visualizado = 1
        }
      } catch (error) {
        console.error('Erro ao marcar notificação como lida:', error)
        throw error
      }
    },

    async obterNotificacao (notificacaoId: string): Promise<Notificacao> {
      try {
        const response = await useApiStore().request(`notificacao/${notificacaoId}`)
        return response
      } catch (error) {
        console.error('Erro ao obter notificação:', error)
        throw error
      }
    },

    getIconForNotification (notificacao: Notificacao): string {
      switch (notificacao.tipo) {
        case 'despesa':
          return 'cil-money'
        case 'receita':
          return 'cil-dollar'
        case 'sistema':
          return 'cil-settings'
        case 'erro':
          return 'cil-warning'
        case 'sucesso':
          return 'cil-check'
        default:
          return 'cil-bell'
      }
    },

    getColorForRelevancia (relevancia: string): string {
      switch (relevancia) {
        case 'ALTA':
          return 'danger'
        case 'MEDIA':
          return 'warning'
        case 'BAIXA':
          return 'info'
        default:
          return 'secondary'
      }
    },

    formatarTempo (data: string): string {
      const agora = new Date()
      const dataNotificacao = new Date(data)
      const diffInMs = agora.getTime() - dataNotificacao.getTime()
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
      const diffInHours = Math.floor(diffInMinutes / 60)
      const diffInDays = Math.floor(diffInHours / 24)

      if (diffInMinutes < 1) {
        return 'Agora'
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m atrás`
      } else if (diffInHours < 24) {
        return `${diffInHours}h atrás`
      } else if (diffInDays < 7) {
        return `${diffInDays}d atrás`
      } else {
        return dataNotificacao.toLocaleDateString('pt-BR')
      }
    },
  },
})
